import React, { useCallback, useState } from 'react';

import MotionWrapper from '@components/wrapper/MotionWrapper';
import styled from 'styled-components';
import CoupleList from './CoupleList';
import PageLayout, { PageHeaderSec } from '@app/layouts/pageLayout/PageLayout';
import { getPathKey } from '@utils/getPath';
import showNav from '@utils/showNav';
import { addCoupleAndGetID } from '@store/firebase/couple/add-couple';
import { useCollection } from '@nandorojo/swr-firestore';
import { IcoupleDetailItem } from '@typings/couple/couple.interface';
import { COUPLE_LAYOUT_TYPE } from '@typings/couple/coupleLayoutType';
import PageControlSec from '@template/ControlSec/PageControlSec';
import { PAGE_LAYOUT_TYPE } from '@typings/pages/pageLayout.interface';
import usePageSet from '@hooks/usePageSet';
import { useFilterLayoutType } from '@hooks/useFilterLayoutType';
import GridDroppable from '@components/library/DnD/GridDroppable';
import CoupleItem from './CoupleList/CoupleItem';
import CoupleCard from './CoupleList/CoupleCard';
import DragDropInfo from '@template/Info/DragDropInfo';
import { AUTH_TYPE } from '@typings/common/authorizationType';
import { formatPixelValue } from '@utils/formatPixelValue';

const Container = styled.div`
  padding: 24px 0 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 24px;
  margin-bottom: 120px;
`;

const ItemContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 32px;
  position: relative;
`;

const initialCoupleData: IcoupleDetailItem = {
  order: 0,
  character: {
    left: {
      name: 'Character1',
    },
    right: {
      name: 'Character2',
    },
  },
  targetDate: new Date(),
  coupleText: '',
  layoutType: COUPLE_LAYOUT_TYPE.GRID3,
  contents: '',
};

function Couple() {
  const [isEdit, setIsEdit] = useState(false);
  const collectionPath = getPathKey(1);

  const openAddPage = useCallback(() => {
    addCoupleAndGetID({
      collectionPath,
      data: initialCoupleData,
    });
  }, [collectionPath]);

  const handleEdit = () => {
    setIsEdit(!isEdit);
  };

  const { data: coupleList } = useCollection<IcoupleDetailItem>(
    `${collectionPath}`,
    {
      listen: true,
      orderBy: ['order', 'asc'],
    },
  );

  // page Setting --------------------------------------------------------
  const { docLayout, docWidth, docLine, writeAuthType } = usePageSet({
    collectionPath,
  });
  const [docType, pageLayoutMap] = useFilterLayoutType({
    docLayout,
    pageSetting: [PAGE_LAYOUT_TYPE.ONELINE, PAGE_LAYOUT_TYPE.EXTEND],
    defaultLayout: PAGE_LAYOUT_TYPE.ONELINE,
  });

  const ListMap = {
    [PAGE_LAYOUT_TYPE.ONELINE]: <CoupleItem />,
    [PAGE_LAYOUT_TYPE.EXTEND]: <CoupleCard />,
  };

  if (!coupleList) return null;

  return (
    <MotionWrapper>
      <PageLayout style={{ maxWidth: formatPixelValue(docWidth, '100%') }}>
        <PageHeaderSec>
          <PageControlSec
            uploadText="추가"
            editText={!isEdit ? '편집' : '편집 완료'}
            handleEdit={handleEdit}
            handleUpload={openAddPage}
            pageLayoutSetting={pageLayoutMap}
            writeAuthType={writeAuthType || AUTH_TYPE.MASTER}
            showLine
            showWidth
            showSearch={false}
          />
        </PageHeaderSec>
        <Container>
          <ItemContainer>
            {!isEdit && (
              <CoupleList
                line={docLine || 2}
                isEdit={isEdit}
                data={coupleList}
                collectionPath={collectionPath}
                boardType={docType}
              />
            )}

            {isEdit && <DragDropInfo />}

            {isEdit && (
              <GridDroppable
                line={docType === PAGE_LAYOUT_TYPE.ONELINE ? 1 : docLine || 2}
                list={coupleList}
                collectionPath={collectionPath}
                isEdit={isEdit}
              >
                {ListMap[docType]}
              </GridDroppable>
            )}
          </ItemContainer>
        </Container>
      </PageLayout>
    </MotionWrapper>
  );
}

export default Couple;
